import React from 'react';

import usePlan from '../../hooks/usePlan';
import DataState, { deriveDataState } from '../loading/DataState';
import Skeleton from '../loading/Skeleton';
import { formatBytes, METER_LABELS } from '../../utils/planLimit';

const NUMBER = new Intl.NumberFormat('en-US');

/** "1.2 GB of 10 GB", "812 of 10,000", "812 · no fixed limit", "Not measured yet". */
export function meterLine({ meter, limit, used }) {
	const show = n => (meter === 'storage' ? formatBytes(n) : NUMBER.format(n));
	const hasUsed = Number.isFinite(used);
	if (Number.isFinite(limit)) return hasUsed ? `${show(used)} of ${show(limit)}` : `Not measured yet · limit ${show(limit)}`;
	return hasUsed ? `${show(used)} · no fixed limit` : 'No fixed limit';
}

/**
 * Account settings › Plan (docs/platform-standards.md §7 "Plan limits";
 * KPR-1095): the account's plan and each meter as "used of limit".
 *
 * Rendered only while plans are visible (config/plans.js) — the Profile page
 * does not mount it otherwise, and `usePlan` asks nothing while the flag is
 * off. Usage is the account's own, across every collection it owns.
 */
export default function PlanSection() {
	const { plan, loading, error, retry } = usePlan();
	const hasData = !!plan;
	return (
		<section className="mt-4 form-panel plan-section" id="plan" aria-labelledby="plan-section-title">
			<h3 id="plan-section-title">Plan</h3>
			<DataState
				state={deriveDataState({ loading, error, hasData })}
				hasData={hasData}
				skeleton={<Skeleton variant="form-field" count={3} />}
				onRetry={retry}
				label="your plan"
			>
				{plan ? (
					<>
						<p className="text-muted small">
							{plan.name}
							{Number.isFinite(plan.fileBytes) ? ` · files up to ${formatBytes(plan.fileBytes)} each` : ''}
						</p>
						<ul className="plan-section__meters">
							{(plan.meters || []).map(row => (
								<li key={row.meter} className="plan-section__meter">
									<span>{METER_LABELS[row.meter] || row.meter}</span>
									<span className="plan-section__amount">{meterLine(row)}</span>
								</li>
							))}
						</ul>
					</>
				) : null}
			</DataState>
		</section>
	);
}
